/**
 * The mention token on the web editor's MESSAGE set (#1599 Story 1.4, AC 3,
 * 5, 6, 9): a one-attribute inline node and its `@` suggester.
 *
 * ⚠️ ONE ATTRIBUTE, NEVER A TAG (AD-4). The token is exactly
 * `<span data-mention-user-id="N">@Name</span>`: the attribute the message
 * sanitizer admits on a `span` (`MENTION_SPAN_ATTRIBUTES`), and nothing else.
 * Three Tiptap 3.29.1 defaults would break that if left alone (story
 * correction 5, measured on the published package):
 *   (a) the default `renderHTML` and `addAttributes` emit `data-type`,
 *       `data-id`, `data-label` and `data-mention-suggestion-char`, every one
 *       of which the sanitizer strips, and the default `parseHTML` matches
 *       `span[data-type="mention"]`, which no stored body carries. All three
 *       are replaced here.
 *   (b) Backspace replaces the node with its trigger character unless
 *       `deleteTriggerWithBackspace` is set, which would leave a bare `@`
 *       and reopen the list. It is set: FR-2 removes the whole token.
 *   (c) the default `command` inserts the node AND a trailing space,
 *       absorbing a space already there. Kept (one space counts toward the
 *       10,000-character limit, which `messageTextLength` already measures).
 *
 * ⚠️ `parseHTML` APPLIES THE SERVER'S ID PREDICATE (`isMentionUserId`,
 * closing deferred-work `:4416`). A span whose id is not a canonical decimal
 * in `[1, 2147483647]` (`__proto__`, `1e3`, `007`) is not parsed as a token
 * at all, so its text stays text and no attribute is emitted, which is what
 * the server's sanitizer would have stored.
 *
 * ⚠️ WHY EVERY LIVE VALUE IS READ THROUGH A REF. The extension list is
 * decided ONCE, at mount (`RichTextEditor.web.tsx`, `useEditor`'s
 * `extensions`), so anything captured when it is built is frozen for the
 * life of the composer. The runtime flag, the candidates and the refused ids
 * change while it is open, so `allow`, `items` and the refusal decoration
 * read `source.current` on every keystroke and every transaction.
 *
 * ⚠️ TWO ATTRIBUTES, EXACTLY ONE PER NODE (#1599 Story 1.7). A person token
 * carries `data-mention-user-id`; the one collective token carries
 * `data-mention-group="config-team"` (`CONFIG_TEAM_GROUP`, mirrored from the
 * server's `configTeamIdentity.ts` and pinned equal). The group attribute is
 * parsed ONLY when its value is that literal, the client mirror of the
 * sanitizer's literal test (the `isMentionUserId` pattern): any other value
 * matches no parse rule, so its text stays text. A span carrying both keeps
 * the user id and drops the group, as the sanitizer does (one token is one
 * target), and `renderHTML` never emits both.
 */
import { Mention, type MentionOptions } from '@tiptap/extension-mention';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

import { MENTION_REFUSED_CLASS } from './richHtmlVisualContract';
import { CONFIG_TEAM_GROUP } from '../correspondence/correspondence.types';
import {
  DISABLED_MENTION_SOURCE,
  filterMentionCandidates,
  type MentionCandidate,
  type MentionSuggestSource,
} from '../correspondence/mentionCandidates';
// The pure module, never `messageHtml` (which pulls `react-native`): the
// 10tap WebView bundle imports this file (#1599 Story 3.1).
import { isMentionUserId } from '../correspondence/mentionUserId';

import type {
  SuggestionKeyDownProps,
  SuggestionProps,
} from '@tiptap/suggestion';
import type { MutableRefObject } from 'react';

export const MENTION_NODE_NAME = 'siteScopeMention';

/**
 * The characters that may precede an `@` for the list to open (story
 * correction 6). The suggestion plugin tests ONE character before the match
 * against `^[${allowedPrefixes.join('')}\0]?$`, and the start of a text node
 * always passes, so the start of the body and of every paragraph opens.
 *
 * ⚠️ THE LIST IS JOINED RAW INTO A REGEX CHARACTER CLASS. `]`, `^` and `\`
 * would break it and are not here. `-` is a range operator anywhere but the
 * two ends, and it is FIRST, not last: the plugin appends `\0` AFTER the
 * joined list (`findSuggestionMatch.ts`), so a trailing `-` made the range
 * `/-\0`, which threw "Range out of order" on the first keystroke (measured
 * 2026-10-22; the story's correction 6 said "last" and was wrong). A letter
 * or digit is deliberately absent: that is what keeps `x@` and
 * `dana@vertiv.com` from opening.
 */
export const MENTION_ALLOWED_PREFIXES: readonly string[] = [
  '-',
  ' ',
  '\t',
  '\n',
  '(',
  '[',
  '{',
  '"',
  "'",
  ',',
  '.',
  ';',
  ':',
  '!',
  '?',
  '/',
];

/**
 * What the composer hands the editor: plain data, defined beside the
 * candidates so the adapter's types file (`TextAreaEditor.types.ts`) never
 * imports a module that pulls Tiptap. Re-exported here for the editor side.
 */
export type { MentionSuggestSource };

/**
 * Defined beside the source type in `mentionCandidates.ts` (#1599 Story 3.1)
 * so the native editor's React Native side can send it without importing
 * Tiptap; re-exported here for the editor side.
 */
export { DISABLED_MENTION_SOURCE };

/** The suggester's UI, driven by the plugin's render lifecycle. */
export interface SuggesterUiHandle {
  open(props: SuggestionProps<MentionCandidate>): void;
  update(props: SuggestionProps<MentionCandidate>): void;
  /** `true` when the key was handled and must not reach the editor. */
  keyDown(props: SuggestionKeyDownProps): boolean;
  close(): void;
}

/** The suggestion plugin's key, exported so tests read its state, not its config. */
export const MENTION_SUGGESTION_KEY = new PluginKey(
  'siteScopeMentionSuggestion',
);

/**
 * The class on a token the server refused (AC 9). Defined in the visual
 * contract beside `mentionRefusedCss` (#1599 Story 3.1), so the native
 * editor's CSS and this decoration name one class; re-exported here.
 */
export { MENTION_REFUSED_CLASS };

/** The meta a transaction carries to re-run the refusal decoration. */
export const MENTION_REFUSAL_META = 'mention-refusal';

interface SiteScopeMentionExtraOptions {
  /** The refused ids, read on every transaction. */
  refusedIds: () => readonly string[];
}

const refusalPluginKey = new PluginKey('siteScopeMentionRefusal');

export const SiteScopeMention = Mention.extend<
  MentionOptions<MentionCandidate> & SiteScopeMentionExtraOptions
>({
  name: MENTION_NODE_NAME,

  addOptions() {
    return {
      ...(this.parent?.() as MentionOptions<MentionCandidate>),
      refusedIds: () => [],
    };
  },

  addAttributes() {
    return {
      userId: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const value = element.getAttribute('data-mention-user-id');
          return value !== null && isMentionUserId(value) ? value : null;
        },
        renderHTML: (attributes: Record<string, unknown>) =>
          typeof attributes.userId === 'string'
            ? { 'data-mention-user-id': attributes.userId }
            : {},
      },
      // #1599 Story 1.7: the team token. Only the one literal, and never on
      // a span whose user id is a token already (one span, one target).
      group: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          !isMentionUserId(
            element.getAttribute('data-mention-user-id') ?? '',
          ) && element.getAttribute('data-mention-group') === CONFIG_TEAM_GROUP
            ? CONFIG_TEAM_GROUP
            : null,
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes.group === CONFIG_TEAM_GROUP
            ? { 'data-mention-group': CONFIG_TEAM_GROUP }
            : {},
      },
      label: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          (element.textContent ?? '').replace(/^@/, ''),
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-mention-user-id]',
        // Not a token unless the id has the server's shape: the span's text
        // is then parsed as ordinary text, attribute gone.
        getAttrs: (element: HTMLElement) =>
          isMentionUserId(element.getAttribute('data-mention-user-id') ?? '')
            ? null
            : false,
      },
      {
        // #1599 Story 1.7: a wrong literal matches no rule and stays text.
        tag: `span[data-mention-group="${CONFIG_TEAM_GROUP}"]`,
      },
    ];
  },

  renderHTML({ node }) {
    const userId = node.attrs.userId as string | null;
    const group = node.attrs.group as string | null;
    // Exactly one attribute: the user id when set, else the group literal.
    const attrs = userId
      ? { 'data-mention-user-id': userId }
      : group === CONFIG_TEAM_GROUP
      ? { 'data-mention-group': CONFIG_TEAM_GROUP }
      : {};
    return ['span', attrs, `@${(node.attrs.label as string | null) ?? ''}`];
  },

  renderText({ node }) {
    return `@${(node.attrs.label as string | null) ?? ''}`;
  },

  addProseMirrorPlugins() {
    const parent = this.parent?.() ?? [];
    const refusedIds = this.options.refusedIds;
    const nodeName = this.name;
    const refusal = new Plugin({
      key: refusalPluginKey,
      props: {
        decorations(state) {
          const refused = refusedIds();
          if (refused.length === 0) return null;
          const wanted = new Set(refused);
          const decorations: Decoration[] = [];
          // Person tokens only: a `group: true` refusal has no outline
          // (#1599 Story 1.7, correction 10, deferred).
          state.doc.descendants((node, pos) => {
            if (
              node.type.name === nodeName &&
              typeof node.attrs.userId === 'string' &&
              wanted.has(node.attrs.userId)
            ) {
              decorations.push(
                Decoration.node(pos, pos + node.nodeSize, {
                  class: MENTION_REFUSED_CLASS,
                }),
              );
            }
          });
          return DecorationSet.create(state.doc, decorations);
        },
      },
    });
    return [...parent, refusal];
  },
});

/**
 * The configured extension for one editor: the node, the suggester wired to
 * `ui`, every live value read from `source`.
 */
export function buildSiteScopeMention(
  source: MutableRefObject<MentionSuggestSource>,
  ui: SuggesterUiHandle,
) {
  return SiteScopeMention.configure({
    deleteTriggerWithBackspace: true,
    refusedIds: () => source.current.refusedIds,
    suggestion: {
      char: '@',
      pluginKey: MENTION_SUGGESTION_KEY,
      allowedPrefixes: [...MENTION_ALLOWED_PREFIXES],
      // Teams behaviour (owner ruling 2026-10-22): the query runs across
      // spaces, so `@Priya Sh` keeps narrowing. Without the `shouldShow`
      // rule below, `allowSpaces` would keep the list open across a whole
      // sentence (the regex runs to the end of the text block).
      allowSpaces: true,
      placement: 'top-start',
      // The list closes once a typed query matches nobody, and comes back if
      // the author deletes back to a match. A bare `@` always shows (so the
      // empty and failed states of frame F5 can speak), and so does a query
      // while the names are still loading or failed to load.
      shouldShow: ({ query }) => {
        const live = source.current;
        if (live.status !== 'ready' || query.trim() === '') return true;
        return filterMentionCandidates(live.candidates, query).length > 0;
      },
      // The flag, read per keystroke, AND the default rule (the node may sit
      // where the caret is: not inside a code block).
      allow: ({ state, range }) => {
        if (!source.current.enabled) return false;
        const $from = state.doc.resolve(range.from);
        const type = state.schema.nodes[MENTION_NODE_NAME];
        return !!type && !!$from.parent.type.contentMatch.matchType(type);
      },
      // Filtering only. Typing never fetches (deferred-work `:4406`): the
      // candidates were fetched once per composer open and space.
      items: ({ query }) =>
        filterMentionCandidates(source.current.candidates, query),
      // The default command's shape (get-default-suggestion-attributes):
      // absorb a following space, insert the node and one space.
      command: ({ editor, range, props }) => {
        const candidate = props as unknown as MentionCandidate;
        // #1599 Story 1.7: a group row inserts the team token.
        const attrs =
          candidate.kind === 'person'
            ? { userId: candidate.userId, label: candidate.name }
            : { group: CONFIG_TEAM_GROUP, label: candidate.name };
        const nodeAfter = editor.view.state.selection.$to.nodeAfter;
        if (nodeAfter?.text?.startsWith(' ')) range.to += 1;
        editor
          .chain()
          .focus()
          .insertContentAt(range, [
            {
              type: MENTION_NODE_NAME,
              attrs,
            },
            { type: 'text', text: ' ' },
          ])
          .run();
      },
      render: () => ({
        onStart: props =>
          ui.open(props as unknown as SuggestionProps<MentionCandidate>),
        onUpdate: props =>
          ui.update(props as unknown as SuggestionProps<MentionCandidate>),
        onKeyDown: props => ui.keyDown(props),
        onExit: () => ui.close(),
      }),
    },
  });
}
